import { useEffect, useState } from "react";
import api from "../../lib/api";
import { currency, fmtDate } from "../../lib/status";

export default function AdminMemberships() {
    const [memberships, setMemberships] = useState([]);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        (async () => {
            const [m, p] = await Promise.all([
                api.get("/membership/all"),
                api.get("/membership/plans"),
            ]);
            setMemberships(m.data);
            setPlans(p.data);
        })();
    }, []);

    const activeCount = memberships.filter((m) => m.status === "active").length;
    const totalRev = memberships
        .filter((m) => m.status === "active" || m.status === "cancelled")
        .reduce((s, m) => {
            const plan = plans.find((p) => p.id === m.plan_id);
            return s + (plan?.monthly_price || 0);
        }, 0);

    return (
        <div className="space-y-6">
            <div>
                <div className="lx-label text-[#D4AF37] mb-2">Subscriptions</div>
                <h1 className="font-serif text-4xl text-white">Memberships</h1>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <KPI label="Active members" value={activeCount} />
                <KPI label="Total subscriptions" value={memberships.length} />
                <KPI label="MRR (gross)" value={currency(totalRev, "AED")} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                {plans.map((p) => {
                    const tierActive = memberships.filter(
                        (m) => m.status === "active" && m.plan_id === p.id,
                    ).length;
                    return (
                        <div key={p.id} className="lx-card-dark p-6">
                            <div className="lx-label text-[#D4AF37] uppercase">{p.tier}</div>
                            <div className="font-serif text-2xl text-white mt-1">{p.name}</div>
                            <div className="text-xs text-white/60 mt-1">
                                {currency(p.monthly_price, p.currency)} / mo · {p.discount_percent}% off
                            </div>
                            <div className="mt-4 pt-4 border-t border-[#2B1438] flex justify-between">
                                <div className="text-sm text-white/60">Active members</div>
                                <div className="font-serif text-xl text-[#D4AF37]">
                                    {tierActive}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="lx-card-dark overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-white/60 border-b border-[#2B1438]">
                        <tr>
                            <th className="p-4 lx-label">Member</th>
                            <th className="p-4 lx-label">Plan</th>
                            <th className="p-4 lx-label">Started</th>
                            <th className="p-4 lx-label">Expires</th>
                            <th className="p-4 lx-label text-right">Saved</th>
                            <th className="p-4 lx-label">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {memberships.map((m) => (
                            <tr
                                key={m.id}
                                className="border-b border-[#2B1438]"
                                data-testid={`mem-row-${m.id}`}
                            >
                                <td className="p-4">
                                    <div className="text-white">{m.user?.name}</div>
                                    <div className="text-xs text-white/60">{m.user?.email}</div>
                                </td>
                                <td className="p-4 text-[#D4AF37] font-serif">{m.plan_name}</td>
                                <td className="p-4 text-white/60 text-xs">
                                    {fmtDate(m.started_at, { hour: undefined, minute: undefined })}
                                </td>
                                <td className="p-4 text-white/60 text-xs">
                                    {fmtDate(m.expires_at, { hour: undefined, minute: undefined })}
                                </td>
                                <td className="p-4 text-right text-[#34C759] font-serif">
                                    {currency(m.total_saved || 0, "AED")}
                                </td>
                                <td className="p-4">
                                    <span
                                        className="text-[10px] uppercase tracking-wider px-2 py-1 border"
                                        style={{
                                            color: m.status === "active" ? "#34C759" : "#A09BA5",
                                            borderColor: m.status === "active" ? "#34C75940" : "#A09BA540",
                                        }}
                                    >
                                        {m.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {memberships.length === 0 && (
                    <div className="p-12 text-center text-white/60">
                        No subscriptions yet.
                    </div>
                )}
            </div>
        </div>
    );
}

function KPI({ label, value }) {
    return (
        <div className="lx-card-dark p-6">
            <div className="lx-label text-[#A09BA5]">{label}</div>
            <div className="font-serif text-3xl text-white mt-2">{value}</div>
        </div>
    );
}
